import type { WorkerMessage, WorkerResponse, WorkerRequest, WorkerEvent, EmulatorState } from './messages';
import type { Chipset, ChipsetSnapshot } from '../chipset/Chipset';
import type { ModelConfig } from '../../model/ModelConfig';
import type { Screen } from '../peripheral/Screen';
import type { Keyboard } from '../peripheral/Keyboard';
import type { StorageProvider } from '../../storage/StorageProvider';
import type { RegisterBank } from '../cpu/registers';
import { createEmulatorFromModel } from '../../model/createEmulator';
import { serializeSnapshot, isSerializedSnapshot, deserializeSnapshot, collectTransferables } from './snapshot-serializer';
import type { SerializedSnapshot } from './snapshot-serializer';
import { createBridgeStorageProvider } from './BridgeStorageProvider';
import { disassembleRange } from '../cpu/disassembler';

type PostMessageFn = (msg: unknown, transferables?: Transferable[]) => void;

interface PendingHostRequest {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

interface BpEntry {
  address: number;
  csr: number;
  condition?: string;
  enabled: boolean;
  hitCount: number;
}

function bpKey(csr: number, address: number): string {
  return `${csr}:${address}`;
}

function evaluateCondition(condition: string, registers: Record<string, number>): boolean {
  const m = condition.match(/^\s*(R\d+|ER\d+|PC|PSW|CSR|SP)\s*(==|!=|<|>|<=|>=)\s*(0x[0-9a-fA-F]+|\d+)\s*$/);
  if (!m) return false;
  const regName = m[1];
  const op = m[2];
  const literalStr = m[3];
  const regValue = registers[regName];
  if (regValue === undefined) return false;
  const literal = literalStr.startsWith('0x') || literalStr.startsWith('0X')
    ? parseInt(literalStr, 16)
    : parseInt(literalStr, 10);
  if (isNaN(literal)) return false;
  switch (op) {
    case '==': return regValue === literal;
    case '!=': return regValue !== literal;
    case '<': return regValue < literal;
    case '>': return regValue > literal;
    case '<=': return regValue <= literal;
    case '>=': return regValue >= literal;
    default: return false;
  }
}

function buildRegisterDict(regs: RegisterBank): Record<string, number> {
  const dict: Record<string, number> = {
    PC: regs.pc,
    CSR: regs.csr,
    PSW: regs.psw,
    SP: regs.sp,
  };
  for (let i = 0; i < 16; i++) {
    dict[`R${i}`] = regs.r[i];
  }
  for (let i = 0; i < 15; i++) {
    dict[`ER${i}`] = (regs.r[i] << 8) | regs.r[i + 1];
  }
  return dict;
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

export class WorkerBridgeWorker {
  private chipset: Chipset | null = null;
  private methodHandlers: Map<string, (...args: unknown[]) => unknown> = new Map();
  private postMessage: PostMessageFn;
  private pendingHostRequests: Map<string, PendingHostRequest> = new Map();
  private hostRequestTimeout: number = 5000;
  private _storageProvider: StorageProvider | null = null;
  private breakpoints: Map<string, BpEntry> = new Map();
  private stepHookInstalled: boolean = false;

  // Tick drive loop — corresponds to CasioEmuNeo's tick_thread + TimerCallback
  // See: CasioEmuNeo/emulator/Emulator.cpp:102-120 (tick_thread), :297-313 (TimerCallback), :439-460 (Cycles::GetDelta)
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private readonly TICK_INTERVAL_MS = 4;
  private readonly TICKS_PER_BATCH = 8000;    // 4ms × 2MHz CLASSWIZ = 8000 cycles
  private readonly STATE_UPDATE_THROTTLE = 4;  // emitStateUpdate every ~16ms = ~60fps
  private stateUpdateCounter = 0;

  constructor(scope?: { postMessage(msg: unknown, transferables?: Transferable[]): void }) {
    this.postMessage = scope
      ? (msg: unknown, transferables?: Transferable[]) => scope.postMessage(msg, transferables)
      : (msg: unknown, transferables?: Transferable[]) => {
          if (transferables && transferables.length > 0) {
            (self.postMessage as (message: unknown, transfer: Transferable[]) => void)(msg, transferables);
          } else {
            self.postMessage(msg);
          }
        };
    this.registerDefaultHandlers();
  }

  registerMethod(name: string, handler: (...args: unknown[]) => unknown): void {
    this.methodHandlers.set(name, handler);
  }

  private registerDefaultHandlers(): void {
    this.registerMethod('init', (config: unknown) => {
      if (this.chipset) {
        this.chipset.destroy();
      }
      const storage = this.createStorageProvider();
      const components = createEmulatorFromModel(config as ModelConfig, storage);
      this.chipset = components.chipset;
      this.ensureStepHook();
      return this.chipset.getState();
    });

    this.registerMethod('chipset.start', () => {
      if (this.chipset) {
        this.ensureStepHook();
        this.chipset.start();      // CasioEmuNeo: Reset + unpause
        this.startTickLoop();      // CasioEmuNeo: tick_thread launch
      }
      return undefined;
    });

    this.registerMethod('chipset.pause', () => {
      this.stopTickLoop();         // CasioEmuNeo: SetPaused(true) — stop driving
      this.chipset?.pause();
      return undefined;
    });

    this.registerMethod('chipset.step', () => {
      this.chipset?.step();
      return undefined;
    });

    this.registerMethod('chipset.reset', () => {
      this.chipset?.reset();
      return undefined;
    });

    this.registerMethod('chipset.getState', () => {
      return this.chipset?.getState() ?? null;
    });

    this.registerMethod('cpu.getRegisters', () => {
      return this.chipset?.cpu.regs ?? null;
    });

    this.registerMethod('screen.getFrameBuffer', () => {
      const screen = this.chipset?.peripheralManager.getPeripheral('Screen') as Screen | undefined;
      return screen?.getFrameBuffer() ?? null;
    });

    this.registerMethod('chipset.setSpeed', (speed: unknown) => {
      this.chipset?.setSpeed(speed as number);
      return undefined;
    });

    this.registerMethod('keyboard.keyPress', (row: unknown, col: unknown) => {
      const kb = this.chipset?.peripheralManager.getPeripheral('Keyboard') as Keyboard | undefined;
      kb?.keyPress(row as number, col as number);
      return undefined;
    });

    this.registerMethod('keyboard.keyRelease', (row: unknown, col: unknown) => {
      const kb = this.chipset?.peripheralManager.getPeripheral('Keyboard') as Keyboard | undefined;
      kb?.keyRelease(row as number, col as number);
      return undefined;
    });

    this.registerMethod('chipset.saveSnapshot', () => {
      return this.chipset?.saveSnapshot() ?? null;
    });

    this.registerMethod('chipset.loadSnapshot', (snapshot: unknown) => {
      this.chipset?.loadSnapshot(snapshot as ChipsetSnapshot);
      return undefined;
    });

    this.registerMethod('chipset.saveSnapshotSerialized', () => {
      const snapshot = this.chipset?.saveSnapshot();
      if (!snapshot) return null;
      return serializeSnapshot(snapshot);
    });

    this.registerMethod('chipset.loadSnapshotSerialized', (serialized: unknown) => {
      const snapshot = deserializeSnapshot(serialized as SerializedSnapshot);
      this.chipset?.loadSnapshot(snapshot);
      return undefined;
    });

    this.registerMethod('chipset.destroy', () => {
      this.stopTickLoop();
      this.chipset?.destroy();
      this.chipset = null;
      this.stepHookInstalled = false;
      return undefined;
    });

    this.registerMethod('debug.disassemble', (csr: unknown, address: unknown, count: unknown) => {
      const c = this.chipset;
      if (!c) return [];
      const csrNum = (csr as number) & 0x0F;
      const readWord = (csrVal: number, addr: number) => {
        const fullAddr = ((csrVal & 0x0F) << 16) | (addr & 0xFFFF);
        return c.mmu.readCode16(fullAddr);
      };
      return disassembleRange(readWord, csrNum, (address as number) & 0xFFFF, count as number);
    });

    this.registerMethod('debug.readMemory', (address: unknown, length: unknown) => {
      const c = this.chipset;
      if (!c) return new Uint8Array(0);
      const addr = address as number;
      const len = length as number;
      const data = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        data[i] = c.mmu.read8(addr + i);
      }
      return data;
    });

    this.registerMethod('debug.writeMemory', (address: unknown, dataBytes: unknown) => {
      const c = this.chipset;
      if (!c) return undefined;
      const addr = address as number;
      const data = (dataBytes as Uint8Array) || new Uint8Array(0);
      for (let i = 0; i < data.length; i++) {
        c.mmu.write8(addr + i, data[i]);
      }
      return undefined;
    });

    this.registerMethod('debug.addBreakpoint', (bp: unknown) => {
      const b = bp as BpEntry;
      this.breakpoints.set(bpKey(b.csr, b.address), {
        address: b.address & 0xFFFFF,
        csr: b.csr & 0x0F,
        condition: b.condition,
        enabled: true,
        hitCount: 0,
      });
      this.ensureStepHook();
      return undefined;
    });

    this.registerMethod('debug.removeBreakpoint', (address: unknown, csr: unknown) => {
      this.breakpoints.delete(bpKey(csr as number, address as number));
      if (this.breakpoints.size === 0 && this.chipset) {
        this.chipset.stepHook = null;
        this.stepHookInstalled = false;
      }
      return undefined;
    });

    this.registerMethod('terminal.execute', (command: unknown) => {
      const c = this.chipset;
      if (!c) return 'Error: chipset not initialized';

      const cpu = {
        get r() { return c.cpu.regs.r; },
        get pc() { return c.cpu.regs.pc; },
        get psw() { return c.cpu.regs.psw; },
        get csr() { return c.cpu.regs.csr; },
        get sp() { return c.cpu.regs.sp; },
        get ea() { return c.cpu.regs.ea; },
        get dsr() { return c.cpu.regs.dsr; },
      };
      const mmu = {
        r8: (addr: number) => c.mmu.read8(addr),
        w8: (addr: number, val: number) => c.mmu.write8(addr, val),
      };
      const chipsetObj = {
        tick: (n: number) => c.tick(n),
        step: () => c.step(),
      };

      try {
        const fn = new Function('cpu', 'mmu', 'chipset', `return (${command as string})`);
        const result = fn(cpu, mmu, chipsetObj);
        if (typeof result === 'number') {
          return `0x${(result >>> 0).toString(16).toUpperCase()} (${result >>> 0})`;
        }
        if (result === undefined) return 'undefined';
        if (result === null) return 'null';
        try {
          return JSON.stringify(result);
        } catch {
          return String(result);
        }
      } catch (err) {
        return `Error: ${err instanceof Error ? err.message : String(err)}`;
      }
    });
  }

  private ensureStepHook(): void {
    if (this.stepHookInstalled || !this.chipset) return;
    this.chipset.stepHook = () => this.checkBreakpoints();
    this.stepHookInstalled = true;
  }

  private checkBreakpoints(): boolean {
    const c = this.chipset;
    if (!c || this.breakpoints.size === 0) return false;

    const pc = c.cpu.regs.pc;
    const csr = c.cpu.regs.csr;
    const entry = this.breakpoints.get(bpKey(csr, pc));
    if (!entry || !entry.enabled) return false;

    if (entry.condition) {
      const registers = buildRegisterDict(c.cpu.regs);
      if (!evaluateCondition(entry.condition, registers)) return false;
    }

    entry.hitCount++;
    this.sendEvent('breakpointHit', {
      address: pc,
      csr,
      hitCount: entry.hitCount,
      registers: c.cpu.regs,
    });
    return true;
  }

  // Corresponds to CasioEmuNeo's tick_thread (Emulator.cpp:102-120)
  // Uses setInterval instead of a dedicated thread + sleep_until
  private startTickLoop(): void {
    if (this.tickTimer !== null) return;
    this.tickTimer = setInterval(() => {
      if (!this.chipset?.isRunning()) return;
      this.chipset.tick(this.TICKS_PER_BATCH);
      this.stateUpdateCounter++;
      if (this.stateUpdateCounter >= this.STATE_UPDATE_THROTTLE) {
        this.emitStateUpdate();    // CasioEmuNeo: SDL_PushEvent(CE_FRAME_REQUEST)
        this.stateUpdateCounter = 0;
      }
    }, this.TICK_INTERVAL_MS);
  }

  private stopTickLoop(): void {
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
    this.stateUpdateCounter = 0;
  }

  onMessage(e: MessageEvent<WorkerMessage>): void {
    const msg = e.data;

    if (msg.type === 'response') {
      const pending = this.pendingHostRequests.get(msg.id);
      if (pending) {
        clearTimeout(pending.timer);
        this.pendingHostRequests.delete(msg.id);
        if (msg.success) {
          pending.resolve(msg.result);
        } else {
          pending.reject(new Error(msg.error || 'Host request error'));
        }
      }
      return;
    }

    if (msg.type !== 'request') {
      return;
    }

    const handler = this.methodHandlers.get(msg.method);
    if (!handler) {
      const response: WorkerResponse = {
        type: 'response',
        id: msg.id,
        success: false,
        error: `Unknown method: ${msg.method}`,
        timestamp: Date.now(),
      };
      this.postMessage(response);
      return;
    }

    try {
      const result = handler(...msg.params);
      const response: WorkerResponse = {
        type: 'response',
        id: msg.id,
        success: true,
        result,
        timestamp: Date.now(),
      };
      if (isSerializedSnapshot(result)) {
        this.postMessage(response, collectTransferables(result));
      } else {
        this.postMessage(response);
      }
    } catch (err) {
      const response: WorkerResponse = {
        type: 'response',
        id: msg.id,
        success: false,
        error: err instanceof Error ? err.message : String(err),
        timestamp: Date.now(),
      };
      this.postMessage(response);
    }
  }

  emitStateUpdate(): void {
    if (!this.chipset) return;

    const screen = this.chipset.peripheralManager.getPeripheral('Screen') as Screen | undefined;
    const state: EmulatorState = {
      frameBuffer: screen?.getFrameBuffer() ?? new Uint8Array(0),
      statusIcons: (screen?.getStatusIcons() ?? {}) as Record<string, boolean>,
      pressedButtons: [],
      isRunning: this.chipset.isRunning(),
      totalTicks: this.chipset.getState().totalTicks,
    };

    this.sendEvent('stateUpdate', state);
  }

  sendEvent(event: string, data: unknown): void {
    const msg: WorkerEvent = {
      type: 'event',
      event,
      data,
      timestamp: Date.now(),
    };
    this.postMessage(msg);
  }

  sendHostRequest(method: string, params: unknown[]): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const id = generateId();
      const request: WorkerRequest = {
        type: 'request',
        id,
        method,
        params,
        timestamp: Date.now(),
      };

      const timer = setTimeout(() => {
        this.pendingHostRequests.delete(id);
        reject(new Error(`Host request '${method}' timed out after ${this.hostRequestTimeout}ms`));
      }, this.hostRequestTimeout);

      this.pendingHostRequests.set(id, { resolve, reject, timer });
      this.postMessage(request);
    });
  }

  createStorageProvider(): StorageProvider {
    if (!this._storageProvider) {
      this._storageProvider = createBridgeStorageProvider(this);
    }
    return this._storageProvider;
  }
}
